import { useState } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useSite } from "../../context/SiteContext";

const inputCls = "w-full bg-white border border-line focus:border-gold outline-none px-4 py-2.5 rounded-sm text-sm focus:ring-2 focus:ring-gold/30";
const labelCls = "block text-xs uppercase tracking-widest text-forest/60 mb-1.5";

function emptyOf(fields) {
  const o = {};
  fields.forEach((f) => {
    if (f.type === "checkbox") o[f.key] = false;
    else if (f.type === "select") o[f.key] = (f.options && f.options[0]) || "";
    else o[f.key] = "";
  });
  return o;
}

export default function ListManager({ resource, title, items, fields, display, testid }) {
  const { reload } = useSite();
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyOf(fields));
  const [saving, setSaving] = useState(false);

  const openNew = () => { setEditing(null); setForm(emptyOf(fields)); setModal(true); };
  const openEdit = (it) => {
    setEditing(it.id);
    const o = {};
    fields.forEach((f) => { o[f.key] = f.type === "list" ? (it[f.key] || []).join(", ") : (it[f.key] ?? (f.type === "checkbox" ? false : "")); });
    setForm(o);
    setModal(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {};
    fields.forEach((f) => {
      if (f.type === "list") payload[f.key] = String(form[f.key] || "").split(",").map((s) => s.trim()).filter(Boolean);
      else payload[f.key] = form[f.key];
    });
    try {
      if (editing) await api.update(resource, editing, payload);
      else await api.create(resource, payload);
      toast.success(editing ? "Updated" : "Added");
      setModal(false);
      await reload();
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.remove(resource, id); toast.success("Deleted"); await reload(); }
    catch { toast.error("Failed to delete"); }
  };

  return (
    <div data-testid={`${testid}-manager`}>
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <h2 className="font-serif text-2xl text-forest">{items.length} {title}</h2>
        <button data-testid={`add-${testid}-btn`} onClick={openNew} className="inline-flex items-center gap-2 bg-gold text-forest px-6 py-3 rounded-full text-sm uppercase tracking-widest hover:-translate-y-0.5 transition-transform">
          <Plus size={16} /> Add {title.replace(/s$/, "")}
        </button>
      </div>

      <div className="border border-line divide-y divide-line bg-white">
        {items.map((it) => (
          <div key={it.id} data-testid={`${testid}-row`} className="flex items-start justify-between gap-6 p-6">
            <div className="min-w-0">{display(it)}</div>
            <div className="flex items-center gap-2 shrink-0">
              <button data-testid={`edit-${testid}-btn`} onClick={() => openEdit(it)} className="w-9 h-9 flex items-center justify-center border border-line rounded-sm hover:bg-forest hover:text-bone transition-colors"><Pencil size={15} /></button>
              <button data-testid={`delete-${testid}-btn`} onClick={() => remove(it.id)} className="w-9 h-9 flex items-center justify-center border border-line rounded-sm hover:bg-destructive hover:text-white transition-colors"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="p-8 text-muted">Nothing here yet.</p>}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-forest-900/60 backdrop-blur-sm" onClick={() => setModal(false)}>
          <form data-testid={`${testid}-form`} onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-bone w-full max-w-lg rounded-sm border border-line p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-serif text-3xl text-forest">{editing ? "Edit" : "Add"} {title.replace(/s$/, "")}</h3>
              <button type="button" onClick={() => setModal(false)} className="text-muted hover:text-forest"><X size={22} /></button>
            </div>
            {fields.map((f) => (
              <div key={f.key} className="mb-5">
                {f.type === "checkbox" ? (
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input data-testid={`${testid}-field-${f.key}`} type="checkbox" checked={!!form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.checked })} className="w-4 h-4 accent-forest" />
                    <span className="text-sm text-forest">{f.label}</span>
                  </label>
                ) : (
                  <>
                    <label className={labelCls}>{f.label}{f.type === "list" && " (comma separated)"}</label>
                    {f.type === "textarea" ? (
                      <textarea data-testid={`${testid}-field-${f.key}`} rows={3} value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className={`${inputCls} resize-none`} />
                    ) : f.type === "select" ? (
                      <select data-testid={`${testid}-field-${f.key}`} value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className={inputCls}>
                        {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input data-testid={`${testid}-field-${f.key}`} value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className={inputCls} />
                    )}
                  </>
                )}
              </div>
            ))}
            <button data-testid={`save-${testid}-btn`} type="submit" disabled={saving} className="w-full bg-forest text-bone py-4 rounded-full text-sm uppercase tracking-widest hover:-translate-y-0.5 transition-transform disabled:opacity-60">
              {saving ? "Saving…" : editing ? "Update" : "Add"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
